import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { BillingStatsApi } from 'components/Apis/KPIs';

const getDraftKey = (scope, id) => `${scope}:${id}`;

const toGoalValue = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = Number(value);
    return Number.isFinite(num) ? num : '';
};

const normalizeGoalKey = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).trim().toLowerCase().replace(/\s+/g, '_');
};

const parseGoalNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const normalized = String(value).replace(/[$,\s]/g, '');
    const num = Number(normalized);
    return Number.isFinite(num) ? num : null;
};

const DEFAULT_GOAL_SETTING_MAP = {
    metric_field_candidates: {
        loads: ['loads_goal', 'load_goal', 'order_count_goal', 'invoice_count_goal'],
        revenue: ['revenue_goal'],
        profit: ['profit_goal', 'margin_goal', 'profit_margin_goal'],
    },
    payload_fields: {
        loads: 'load_goal',
        revenue: 'revenue_goal',
        profit: 'profit_goal',
    },
    scope_entity_id_transforms: {
        company: { mode: 'literal' },
        customer: { mode: 'tail_lower', strip_prefix: 'customer_' },
        segment: { mode: 'tail_lower', strip_prefix: 'ref_3_' },
        member: { mode: 'tail_lower', strip_prefix: 'team_member_' },
    },
};

const getGoalSettingMap = (billingPeriodData) => ({
    ...DEFAULT_GOAL_SETTING_MAP,
    ...(billingPeriodData?.goal_setting_map || {}),
});

const metricFieldCandidates = (goalSettingMap, goalType) => {
    const payloadField = goalSettingMap?.payload_fields?.[goalType];
    const aliases = goalSettingMap?.metric_field_candidates?.[goalType] || [];
    return Array.from(new Set([payloadField, ...aliases].filter(Boolean)));
};

const extractGoalValueWithMap = (item, goalType, goalSettingMap, goalRecord = null) => {
    const source = goalRecord || item || {};
    for (const fieldName of metricFieldCandidates(goalSettingMap, goalType)) {
        if (Object.prototype.hasOwnProperty.call(source, fieldName)) {
            return toGoalValue(source[fieldName]);
        }
    }
    return '';
};

const transformScopeItemId = (itemId, scope, goalSettingMap) => {
    const transformRule = goalSettingMap?.scope_entity_id_transforms?.[scope] || {};
    const mode = transformRule.mode || 'tail_lower';
    const rawId = String(itemId ?? '');

    if (mode === 'literal') {
        return rawId;
    }

    let normalizedId = rawId;
    if (transformRule.strip_prefix) {
        const escapedPrefix = String(transformRule.strip_prefix).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const stripRegex = new RegExp(`^${escapedPrefix}`, 'i');
        normalizedId = normalizedId.replace(stripRegex, '');
    }

    if (mode === 'tail_lower') {
        const parts = normalizedId.split('_').filter(Boolean);
        normalizedId = (parts.length ? parts[parts.length - 1] : normalizedId).toLowerCase();
    }

    return normalizedId;
};

const getScopeGoalMap = (billingPeriodData, scope) => {
    if (scope === 'company') {
        return (
            billingPeriodData?.fields?.goals?.company ||
            billingPeriodData?.company_goals ||
            {}
        );
    }
    if (scope === 'customer') {
        return (
            billingPeriodData?.customer_goals ||
            billingPeriodData?.fields?.goals?.customer ||
            {}
        );
    }
    if (scope === 'segment') {
        return (
            billingPeriodData?.customer_segment_goals ||
            billingPeriodData?.fields?.goals?.customer_segment ||
            {}
        );
    }
    if (scope === 'member') {
        return (
            billingPeriodData?.service_team_goals ||
            billingPeriodData?.fields?.goals?.service_team ||
            {}
        );
    }
    return {};
};

const findGoalRecordForItem = (billingPeriodData, scope, item, goalSettingMap) => {
    if (!item) return null;
    const goalMap = getScopeGoalMap(billingPeriodData, scope);
    if (!goalMap || typeof goalMap !== 'object') return null;

    if (scope === 'company') {
        return (
            goalMap?.company ||
            goalMap?.[item?.id] ||
            billingPeriodData?.company ||
            null
        );
    }

    const transformedId = transformScopeItemId(item?.id, scope, goalSettingMap);
    const candidates = [
        transformedId,
        String(item?.id || ''),
        String(item?.id || '').toLowerCase(),
        normalizeGoalKey(item?.label),
    ].filter(Boolean);
    if (item?.label == null || String(item?.label).trim() === '') {
        candidates.push('none');
    }

    for (const key of candidates) {
        if (goalMap[key]) return goalMap[key];
    }

    return null;
};

const buildGoalPayload = (scope, item, draftValues = {}, goalSettingMap) => {
    const payloadFields = goalSettingMap?.payload_fields || {};
    const payload = {
        id: transformScopeItemId(item?.id, scope, goalSettingMap),
        label: item?.label || '',
    };

    if (scope === 'company') {
        payload.id = item?.id || 'company';
        payload.label = item?.label || 'Company';
    }

    const metricToDraftKey = {
        loads: 'loads',
        revenue: 'revenue',
        profit: 'profit',
    };
    Object.entries(metricToDraftKey).forEach(([metricKey, draftKey]) => {
        const payloadField = payloadFields[metricKey];
        if (!payloadField) return;
        payload[payloadField] = parseGoalNumber(draftValues[draftKey]) ?? 0;
    });

    return payload;
};

const buildGoalDrafts = (billingPeriodData) => {
    const goalSettingMap = getGoalSettingMap(billingPeriodData);
    const drafts = {};
    const company = billingPeriodData?.company;
    if (company?.id) {
        const draftKey = getDraftKey('company', company.id);
        const companyGoalRecord = findGoalRecordForItem(
            billingPeriodData,
            'company',
            company,
            goalSettingMap
        );
        drafts[draftKey] = {
            loads: extractGoalValueWithMap(company, 'loads', goalSettingMap, companyGoalRecord),
            revenue: extractGoalValueWithMap(company, 'revenue', goalSettingMap, companyGoalRecord),
            profit: extractGoalValueWithMap(company, 'profit', goalSettingMap, companyGoalRecord),
        };
    }

    const hydrate = (items, scope) => {
        (items || []).forEach((item) => {
            if (!item?.id) return;
            const draftKey = getDraftKey(scope, item.id);
            const goalRecord = findGoalRecordForItem(
                billingPeriodData,
                scope,
                item,
                goalSettingMap
            );
            drafts[draftKey] = {
                loads: extractGoalValueWithMap(item, 'loads', goalSettingMap, goalRecord),
                revenue: extractGoalValueWithMap(item, 'revenue', goalSettingMap, goalRecord),
                profit: extractGoalValueWithMap(item, 'profit', goalSettingMap, goalRecord),
            };
        });
    };

    hydrate(billingPeriodData?.customers, 'customer');
    hydrate(billingPeriodData?.customer_segmentation, 'segment');
    hydrate(billingPeriodData?.team_member_contributions, 'member');
    return drafts;
};

export const fetchBillingPeriodOptions = createAsyncThunk(
    'KPIs/fetchBillingPeriodOptions',
    async (_, { rejectWithValue }) => {
        try {
            const response = await BillingStatsApi.getBillingPeriodOptions();
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: error.message });
        }
    }
);

export const fetchBillingPeriod = createAsyncThunk(
    'KPIs/fetchBillingPeriod',
    async (id, { rejectWithValue }) => {
        try {
            let response;
            if (!id) {
                response = await BillingStatsApi.getPreviousWeek();
            }
            else {
                response = await BillingStatsApi.getBillingPeriod(id);
            }
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: error.message });
        }
    }
);

export const fetchPreviousWeekBillingPeriod = createAsyncThunk(
    'KPIs/fetchPreviousWeekBillingPeriod',
    async (_, { rejectWithValue }) => {
        try {
            const response = await BillingStatsApi.getPreviousWeek();
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: error.message });
        }
    }
);

export const updateGoal = createAsyncThunk(
    'KPIs/updateGoal',
    async ({ scope, item, goalType, value }, { getState, rejectWithValue }) => {
        try {
            const state = getState().KPIs;
            const periodId =
                state.billingPeriod?.data?.billing_period?.id ??
                state.billingPeriod?.data?.billing_period_id ??
                state.billingPeriod?.data?.id;
            if (!periodId || !item?.id) {
                throw new Error(`Missing billing period or item id (periodId=${periodId}, itemId=${item?.id})`);
            }

            const draftKey = getDraftKey(scope, item.id);
            const stateDraftValues = state.goalDrafts?.[draftKey] || {};
            const draftValues = value !== undefined
                ? { ...stateDraftValues, [goalType]: value }
                : stateDraftValues;

            if (draftValues?.[goalType] !== undefined) {
                const parsedValue = parseGoalNumber(draftValues[goalType]);
                if (draftValues[goalType] !== '' && parsedValue === null) {
                    return { cellKey: `${draftKey}:${goalType}` };
                }
            }

            const goalSettingMap = getGoalSettingMap(state.billingPeriod?.data);
            const payload = buildGoalPayload(scope, item, draftValues, goalSettingMap);
            let response;
            if (scope === 'company') {
                response = await BillingStatsApi.assignCompanyGoal(periodId, payload);
            } else if (scope === 'customer') {
                response = await BillingStatsApi.assignCustomerGoal(periodId, payload);
            } else if (scope === 'segment') {
                response = await BillingStatsApi.assignCustomerSegmentGoal(periodId, payload);
            } else if (scope === 'member') {
                response = await BillingStatsApi.assignServiceTeamGoal(periodId, payload);
            } else {
                throw new Error(`Unsupported goal scope: ${scope}`);
            }

            return {
                cellKey: `${draftKey}:${goalType}`,
                response: response?.data || null,
            };
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: error.message });
        }
    }
);

const KPIsSlice = createSlice({
    name: 'KPIs',
    initialState: {
        billingPeriod: {
            data: null,
            loading: false,
            error: null,
        },
        billingPeriodOptions: {
            data: [],
            loading: false,
            error: null,
        },
        goalDrafts: {},
        savingGoalByCell: {},
    },
    reducers: {
        setGoalDraft: (state, action) => {
            const { draftKey, goalType, value } = action.payload;
            state.goalDrafts[draftKey] = {
                ...(state.goalDrafts[draftKey] || {}),
                [goalType]: value,
            };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchBillingPeriodOptions.pending, (state) => {
                state.billingPeriodOptions.loading = true;
                state.billingPeriodOptions.error = null;
            })
            .addCase(fetchBillingPeriodOptions.fulfilled, (state, action) => {
                state.billingPeriodOptions.loading = false;
                state.billingPeriodOptions.data = action.payload;
            })
            .addCase(fetchBillingPeriodOptions.rejected, (state, action) => {
                state.billingPeriodOptions.loading = false;
                state.billingPeriodOptions.error = action.payload;
            })
            .addCase(fetchBillingPeriod.pending, (state) => {
                state.billingPeriod.loading = true;
                state.billingPeriod.error = null;
            })
            .addCase(fetchBillingPeriod.fulfilled, (state, action) => {
                state.billingPeriod.loading = false;
                state.billingPeriod.data = action.payload;
                state.goalDrafts = buildGoalDrafts(action.payload);
            })
            .addCase(fetchBillingPeriod.rejected, (state, action) => {
                state.billingPeriod.loading = false;
                state.billingPeriod.error = action.payload;
            })
            .addCase(fetchPreviousWeekBillingPeriod.pending, (state) => {
                state.billingPeriod.loading = true;
                state.billingPeriod.error = null;
            })
            .addCase(fetchPreviousWeekBillingPeriod.fulfilled, (state, action) => {
                state.billingPeriod.loading = false;
                state.billingPeriod.data = action.payload;
                state.goalDrafts = buildGoalDrafts(action.payload);
            })
            .addCase(fetchPreviousWeekBillingPeriod.rejected, (state, action) => {
                state.billingPeriod.loading = false;
                state.billingPeriod.error = action.payload;
            })
            .addCase(updateGoal.pending, (state, action) => {
                const { scope, item, goalType } = action.meta.arg || {};
                const cellKey = `${getDraftKey(scope, item?.id)}:${goalType}`;
                state.savingGoalByCell[cellKey] = true;
            })
            .addCase(updateGoal.fulfilled, (state, action) => {
                const cellKey = action.payload?.cellKey;
                if (cellKey) {
                    state.savingGoalByCell[cellKey] = false;
                }
            })
            .addCase(updateGoal.rejected, (state, action) => {
                const { scope, item, goalType } = action.meta.arg || {};
                const cellKey = `${getDraftKey(scope, item?.id)}:${goalType}`;
                state.savingGoalByCell[cellKey] = false;
            });
    },
});

export const { setGoalDraft } = KPIsSlice.actions;
export default KPIsSlice.reducer;
